/**
 * Hub Service by karora on 11/03/17.
 */
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { BoilerDetails } from '../types/boiler-details';
import { DatabaseQuery } from '../types/database-query';
import { EcmsEntry } from '../types/ecms-entry';
import { FirmwareDetails } from '../types/firmware-details';
import { FirmwareRoute } from '../types/firmware-route';
import { FirmwareSet } from '../types/firmware-set';
import { FuelFill } from '../types/fuel-fill';
import { GlitchLogEntry } from '../types/glitch-log-entry';
import { Hub } from '../types/hub';
import { HubAction } from '../types/hub-action';
import { HubCount } from '../types/hub-count';
import { HubInformation } from '../types/hub-information';
import { HubMessage } from '../types/hub-message';
import { HubSpendInfo } from '../types/hub-spend';
import { HubState } from '../types/hub-state';
import { HubComms } from '../types/hubcomms';
import { HubHealth } from '../types/hubhealth';
import { HubIssue } from '../types/hubissue';
import { Installation } from '../types/installation';
import { IssueAction } from '../types/issue-action';
import { IssueCount } from '../types/issue-count';
import { IssueTagCount } from '../types/issues-tag-count';
import { LeadDetails } from '../types/leads';
import { MeterReading } from '../types/meter-reading';
import { Pairing } from '../types/pairing';
import { QuestionAnswer } from '../types/question-answer';
import { RebootLog } from '../types/reboot-log';
import { ScheduleEvent } from '../types/schedule-event';
import { StandardResponse } from '../types/standard-response';
import { VersionCount } from '../types/version-count';
import { ApiService } from './api.service';


@Injectable()
export class HubService {
  static get BaseURL(): string {
    return HubService._BaseURL;
  }
  private static _BaseURL = '/api/private/';
  private allHubsURL = HubService._BaseURL + 'hubs/all';
  private newHubsURL = HubService._BaseURL + 'hubs/new?limit=1000&verified=false';
  private operationalHubsURL = HubService._BaseURL + 'hubs/operational';
  private hubCountsURL = HubService._BaseURL + 'hubs/health_counts';
  private versionCountsURL = HubService._BaseURL + 'hubs/version_counts';
  private oneHubURL = HubService._BaseURL + 'hub/';
  private lookup_values: Object[][];
  private lookup_refreshed: number[];

  private static hubURL(hubid: number): string {
    return HubService._BaseURL + (hubid ? `hub/${hubid}` : 'hubs');
  }

  private static extractHubResponseData(response: StandardResponse): Hub[] {
    if (response.status === 'error') {
      throw new Error('extractResponseData: ' + response.data.message);
    }
    let hubs = response.list as Hub[];
    for (let i = 0; i < hubs.length; i++) {
      if (hubs[i].hubid && !hubs[i].serial) {
        hubs[i].serial = Hub.HubIDToSerial(hubs[i].hubid);
      }
      if (!hubs[i].gap && hubs[i].interval) {
        hubs[i].gap = hubs[i].interval;
      }
    }
    return hubs;
  }

  private static getError(error: Response | any): Observable<any> {
    let errMsg: string;
    try {
      const body = error || '';
      const err = body.error || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
      console.error(errMsg);
      // return throwError(errMsg);
    } catch (e) {
      console.log('Error in HubService.getError?!');
      console.error(e);
    }
    return of([]);
  }

  private static updateError(error: HttpErrorResponse): string {
    if (error.status !== 401) {
      console.log('Update error: ', error);
    } else if (error.status === 401) {
      console.log('Permission denied: ', error);
    } else {
      console.log('Update error status ' + error.status + ': ', error);
    }
    let errMsg: string;
    try {
      const body = error.error.json() || '';
      const err = body.data.error || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
    } catch (e) {
      errMsg = error.message ? error.message : error.toString();
    }
    console.error(errMsg);
    return errMsg;
  }

  public static LogRefreshError(err: any): void {
    try {
      if (err.status && err.status === 401) {
        console.log(`Permission denied on refresh`);
        ApiService.AuthenticationExpired();
      } else {
        console.log(`Refresh Error: ${err}`);
      }
    } catch (e) {
      console.log(e);
    }
  }

  private static extractStandardList(body: StandardResponse): any {
    if (body.status === 'error') {
      throw new Error(body.data.message);
    }
    return body.list || {};
  }

  static LogFetchError(error: HttpErrorResponse): Observable<any> {
    let errMsg: string;
    try {
      const body = error.error || '';
      const err = `${body.data.attributes.message} (${body.data.id})`;
      errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
      console.error(errMsg);
    } catch (e) {
      console.log(error);
      console.log(JSON.stringify(error, null, 2));
      console.log(e);
    }
    return throwError(errMsg);
  }

  static gotHub(body: StandardResponse): any {
    // console.log("Got Hub ");
    if (body.status === 'error') {
      throw new Error('extractResponseData: ' + body.data.message);
    }
    return body.data || {};
  }

  private static headersFromString(l: string): { key: string, value: string } {
    let parts = l.split(/:\s*/, 2);
    if (!parts) {
      return { key: undefined, value: undefined };
    }
    if (parts.length === 1) {
      return { key: parts[0], value: '' };
    }
    return { key: parts[0], value: parts[1] };
  }

  constructor(
    private api: ApiService,
  ) { }

  getHubs(limit: number, version: string, health: string, fromHubID: number): Observable<Hub[]> {
    let hubsURL = this.allHubsURL;
    if (!limit) {
      limit = 2000;
    }
    hubsURL += `?limit=${limit}`;
    if (!!version) {
      hubsURL += '&version=' + version;
    }
    if (!!health) {
      hubsURL += '&health=' + health;
    }
    if (!!fromHubID) {
      hubsURL += '&from_hub=' + fromHubID;
    }
    // console.log("Getting Hubs");
    return this.api.Get(hubsURL).pipe(
      map(HubService.extractHubResponseData),
      catchError(HubService.getError)
    );
  }

  getNewHubs(): Observable<Hub[]> {
    // console.log("Getting New Hubs");
    return this.api.Get(this.newHubsURL).pipe(
      map(HubService.extractHubResponseData),
      catchError(HubService.getError),
    );
  }

  getWireModeChangedHubs(): Observable<Hub[]> {
    // console.log("Getting Hubs where the wire mode has changed");
    return this.api.Get(HubService._BaseURL + 'hubs/wire_mode_changed').pipe(
      map(HubService.extractHubResponseData),
      catchError(HubService.getError)
    );
  }

  getOperationalHubs(limit: number, fromHubID: number): Observable<Hub[]> {
    console.log('Getting Operational Hubs');
    let hubsURL = this.operationalHubsURL;
    if (!limit) {
      limit = 1000;
    }
    hubsURL += `?limit=${limit}`;
    if (!!fromHubID) {
      hubsURL += '&from_hub=' + fromHubID;
    }
    return this.api.Get(hubsURL).pipe(
      map(HubService.extractHubResponseData),
      catchError(HubService.getError)
    );
  }

  // getHub will return a single Hub, from either a hubID or a machineID
  getHub(hubid: number | string): Observable<Hub> {
    return this.api.Get(this.oneHubURL + hubid).pipe(
      map(HubService.gotHub),
      catchError(HubService.getError)
    );
  }

  getHubInformation(hubid: number): Observable<HubInformation> {
    return this.api.Get(this.oneHubURL + hubid + '/information').pipe(
      map(response => response.data as HubInformation),
      catchError(HubService.getError)
    );
  }

  getStockInformation(sortby: string, sortvalue: string, ascdesc: string): Observable<HubInformation[]> {
    console.log(HubService.BaseURL + `stock?sortby=${sortby}&sortvalue=${sortvalue}&ascdesc=${ascdesc}`);
    return this.api.Get(HubService.BaseURL + `stock?sortby=${sortby}&sortvalue=${sortvalue}&ascdesc=${ascdesc}`).pipe(
      map(response => response.list as HubInformation[]),
      catchError(HubService.getError)
    );
  }

  getInstallerSummary(): Observable<Object[]> {
    console.log(HubService.BaseURL + `stock_installers`);
    return this.api.Get(HubService.BaseURL + `stock_installers`).pipe(
      map(response => response.list as HubInformation[]),
      catchError(HubService.getError)
    );
  }

  getLocationSummary(): Observable<Object[]> {
    console.log(HubService.BaseURL + `stock_locations`);
    return this.api.Get(HubService.BaseURL + `stock_locations`).pipe(
      map(response => response.list as HubInformation[]),
      catchError(HubService.getError)
    );
  }

  getOfficeSummary(): Observable<Object[]> {
    console.log(HubService.BaseURL + `stock_office`);
    return this.api.Get(HubService.BaseURL + `stock_office`).pipe(
      map(response => response.list as HubInformation[]),
      catchError(HubService.getError)
    );
  }

  getHubState(hubid: number): Observable<HubState> {
    return this.api.Get(this.oneHubURL + hubid + '/state').pipe(
      map(response => response.data as HubState),
      catchError(HubService.getError)
    );
  }

  getHubSpend(hubid: number): Observable<HubSpendInfo> {
    return this.api.Get(this.oneHubURL + hubid + '/spend').pipe(
      map(response => response.data as HubSpendInfo),
      catchError(HubService.getError)
    );
  }

  getHubBoiler(hubid: number): Observable<BoilerDetails> {
    return this.api.Get(this.oneHubURL + hubid + '/boiler').pipe(
      map(response => response.data as BoilerDetails),
      catchError(HubService.getError)
    );
  }

  getHubFills(hubid: number): Observable<FuelFill[]> {
    return this.api.Get(this.oneHubURL + hubid + '/fuel_fills').pipe(
      map(response => response.list as FuelFill),
      catchError(HubService.getError)
    );
  }

  getHealth(hubid: number, until: string, from: string): Observable<HubHealth[]> {
    // console.log("Getting Health " + hubid);
    let issuesURL = HubService.hubURL(hubid) + '/health';
    if (!!until) {
      issuesURL += '?until=' + until;
    }
    if (!!from) {
      issuesURL += (until ? '&' : '?') + 'from=' + from;
    }
    return this.api.Get(issuesURL).pipe(
      map(response => response.list as HubHealth[]),
      catchError(HubService.getError)
    );
  }

  getIssues(hubid: number, type: number, until: string, from: string): Observable<HubIssue[]> {
    let issuesURL = HubService.hubURL(hubid) + `/issues`;

    //if(type != 0){
    issuesURL += '?issuetype=' + type.toString();
    console.log("issuesURL...")
    console.log(issuesURL)
    //}

    if (!!until) {
      issuesURL += '&until=' + until;
    }
    if (!!from) {
      issuesURL += '&from=' + from;
    }

    console.log(issuesURL)

    return this.api.Get(issuesURL).pipe(
      map(response => response.list as HubIssue[]),
      catchError(HubService.getError)
    );
  }

  getMessages(hubid: number, maxID: number, from: string): Observable<HubMessage[]> {
    if (!!!hubid) {
      hubid = 0;
    }
    console.log("Getting Messages: " + hubid);

    let fetchURL = HubService.hubURL(hubid) + `/messages`;

    if (!!maxID) {
      fetchURL += '?maxid=' + maxID;
    }
    if (!!from) {
      fetchURL += (maxID ? '&' : '?') + 'from=' + from;
    }

    return this.api.Get(fetchURL).pipe(
      map(response => response.list as HubMessage[]),
      catchError(HubService.getError)
    );
  }

  getComms(hubid: number, until: string): Observable<HubComms[]> {
    let commsURL = HubService.hubURL(hubid) + '/comms';
    if (until) {
      commsURL += '?until=' + until;
    }
    return this.api.Get(commsURL).pipe(
      map(response => this.gotComms(response)),
      catchError(HubService.getError)
    );
  }

  gotComms(response: StandardResponse): HubComms[] {
    let comms = response.list as HubComms[];
    for (let i = 0; i < comms.length; i++) {
      let comm = comms[i];
      let parts = comm.payload.split(/\r?\n\r?\n/m, 2);
      // console.log(`parts:\n${parts[0]}\n=========================\n${parts[1]}\n`);
      try {
        comm.headers = parts[0].split(/\r?\n/m)
          .map((l: string) => HubService.headersFromString(l));
        // console.log('Headers: ' + JSON.stringify(comm.headers));

        if (parts.length > 1) {
          if (parts[1].slice(0, 1) === '{' || parts[1].slice(0, 1) === '[') {
            comm.body = JSON.parse(parts[1]);
          } else {
            comm.body = parts[1];
          }
        } else {
          console.log(`No body parts!`);
        }
        // console.log('WTG!!! body: ' + JSON.stringify(comm.body));
      } catch (e) {
        console.log(`${comm.api} ${comm.typeid}: ${e}`);
      }
      comms[i] = comm;
    }
    return comms;
  }

  getIssueActions(issue_id: number) {
    let issueActionsURL = HubService.BaseURL + 'issue/' + issue_id + '/actions';
    return this.api.Get(issueActionsURL).pipe(
      map(response => response.list as IssueAction[]),
      catchError(HubService.getError)
    );
  }

  getSchedule(hubid: number): Observable<ScheduleEvent[]> {
    console.log('Getting Schedule ' + hubid);
    let scheduleURL = HubService.hubURL(hubid) + '/schedule';
    return this.api.Get(scheduleURL).pipe(
      map(response => response.list as ScheduleEvent[]),
      catchError(HubService.getError)
    );
  }

  clearSchedule(hubid: number): Observable<void> {
    console.log('Clearing Schedule ' + hubid);
    let clearScheduleURL = HubService.hubURL(hubid) + '/schedule/clear';
    return this.api.Get(clearScheduleURL).pipe(
      map(response => response.data as any),
      catchError(HubService.getError)
    );
  }

  deleteEvent(hubid: number, eventUID: number): Observable<void> {
    console.log('Deleting event ' + eventUID + ' for hub ' + hubid);
    let deleteEventURL = HubService.hubURL(hubid) + '/schedule/' + eventUID;
    return this.api.Delete(deleteEventURL).pipe(
      map(response => response.data as any),
      catchError(HubService.getError)
    );
  }

  getSpendAnswers(hubid: number): Observable<QuestionAnswer[]> {
    console.log('Getting Schedule ' + hubid);
    let scheduleURL = HubService.hubURL(hubid) + '/answers';
    return this.api.Get(scheduleURL).pipe(
      map(response => response.list as QuestionAnswer[]),
      catchError(HubService.getError)
    );
  }

  getMeterReadings(hubid: number, until: string): Observable<MeterReading[]> {
    let metersURL = HubService.hubURL(hubid) + `/meter_readings`;
    if (until) {
      metersURL += '?until=' + until;
    }
    return this.api.Get(metersURL).pipe(
      map(response => response.list as MeterReading[]),
      catchError(HubService.getError)
    );
  }

  saveMeterReading(reading: MeterReading): Observable<MeterReading | string> {
    const url = HubService.hubURL(reading.hubid) + `/meter_readings/${reading.uid}`;
    reading.reading = Number(reading.reading);
    return this.api.Put(url, JSON.stringify(reading)).pipe(
      map(() => reading),
      catchError(HubService.updateError)
    );
  }

  getGlitchLogs(hubid: number, until: string): Observable<GlitchLogEntry[]> {
    let glitchesURL = HubService.hubURL(hubid) + `/glitches`;
    if (until) {
      glitchesURL += '?until=' + until;
    }
    return this.api.Get(glitchesURL).pipe(
      map((response: StandardResponse) => response.list as GlitchLogEntry[]),
      catchError(HubService.getError)
    );
  }

  getHubActions(hubid: number, until: Date): Observable<HubAction[]> {
    let actionsURL = HubService.hubURL(hubid) + `/actions`;
    if (until) {
      actionsURL += '?until=' + until.toISOString();
    }
    return this.api.Get(actionsURL).pipe(
      map(response => response.list as HubAction[]),
      catchError(HubService.getError)
    );
  }

  getRebootLogs(hubid: number, until: string, limit: number): Observable<RebootLog[]> {
    let rebootsURL = HubService.hubURL(hubid) + `/reboots`;
    rebootsURL += '?limit=' + (limit > 0 ? limit : 20);
    if (until) {
      rebootsURL += '&until=' + until;
    }
    if (limit > 0)
      return this.api.Get(rebootsURL).pipe(
        map((response: StandardResponse) => response.list as RebootLog[]),
        catchError(HubService.getError)
      );
  }

  getPairings(hubid: number): Observable<Pairing[]> {
    return this.api.Get(HubService.hubURL(hubid) + `/pairings`).pipe(
      map((response: StandardResponse) => response.list as Pairing[]),
      catchError(HubService.getError)
    );
  }

  togglePairing(pairing: Pairing): Observable<StandardResponse> {
    console.log(`Revoking Or Unrevoking Pairing code ${pairing}`);
    let body = JSON.stringify(pairing);
    console.log(body);
    return this.api.Post(HubService.BaseURL + 'revokepairing', body).pipe(map((res) => <StandardResponse>(res)));
  }

  getInstallation(hubid: number): Observable<Installation> {
    return this.api.Get(HubService.hubURL(hubid) + `/installation`).pipe(
      map((response: StandardResponse) => response.data as Installation),
      catchError(HubService.getError)
    );
  }

  getInstallations(until: string): Observable<Installation[]> {
    let installsURL = HubService.BaseURL + `installations`;
    if (until) {
      installsURL += '?from=' + until;
    }
    return this.api.Get(installsURL).pipe(
      map((response: StandardResponse) => response.list as Installation[]),
      catchError(HubService.getError)
    );
  }

  getFirmwareSets(): Observable<FirmwareSet[]> {
    console.log(`Getting Firmware Sets `);
    return this.api.Get(HubService.BaseURL + 'firmwaresets').pipe(
      map(response => response.list as FirmwareSet[]),
      catchError(HubService.getError)
    );
  }

  getFirmwareRoutes(fwsetid: number): Observable<FirmwareRoute[]> {
    console.log(`Getting Firmware Routes for fwset ${fwsetid}`);
    return this.api.Get(HubService.BaseURL + 'firmwareroutes/' + fwsetid).pipe(
      map(response => response.list as FirmwareRoute[]),
      catchError(HubService.getError)
    );
  }

  deleteFirmwareRoute(fwRouteID: number): Observable<boolean> {
    console.log(`Deleting Firmware Route ID ${fwRouteID}`);
    return this.api.Delete(HubService.BaseURL + 'firmwareroute/' + fwRouteID).pipe(
      map(() => true),
      catchError(HubService.getError)
    );
  }

  getFirmwareDetails(): Observable<FirmwareDetails[]> {
    console.log('Getting Firmware Details');
    return this.api.Get(HubService.BaseURL + 'firmwares').pipe(
      map(response => response.list as FirmwareDetails[]),
      catchError(HubService.getError)
    );
  }

  getLookupTable(tableName: string): Observable<Object[]> {
    if (this.lookup_values === undefined) {
      this.lookup_values = Array();
      this.lookup_refreshed = Array();
    } else {
      let recently = new Date().getUTCSeconds();
      recently -= 600;
      if (!!this.lookup_refreshed[tableName] && this.lookup_refreshed[tableName] > recently) {
        return of(this.lookup_values[tableName]);
      }
    }
    return this.api.Get(HubService.BaseURL + 'lookups/' + tableName).pipe(
      map((data) => this.gotLookupTable(tableName, data)),
      catchError(HubService.getError)
    );
  }

  gotLookupTable(tableName: string, response: StandardResponse): Object[] {
    try {
      if (!response.status || response.status !== 'success') {
        console.log('Error response to /lookups/' + tableName + ' request :-(');
      } else {
        // console.log('Response looks good: ' + asJson.status);
        if (!!response.list) {
          // console.log('Response data is present: ' + JSON.stringify(asJson.data));
          this.lookup_values[tableName] = response.list;
          this.lookup_refreshed[tableName] = new Date().getUTCSeconds();
          return this.lookup_values[tableName];
        } else {
          console.log('No lookup data in /lookups/' + tableName + ' response :-(');
        }
      }
    } catch (e) {
      console.log('getLookupTable(' + tableName + '): Error parsing response:  ' + e);
      console.log('response was:\n' + JSON.stringify(response, null, 2));
    }
    console.log('Returning empty lookup table!');
    return <Object[]>[];
  }

  writeInformation(info: HubInformation): Observable<HubInformation> {
    let url = `${this.oneHubURL}` + info.hubid + '/information';

    if (info.stock_status_id) {
      info.stock_status_id = Number(info.stock_status_id);
      if (info.stock_status_id < 1) {
        info.stock_status_id = 1;
      }
    }

    if (info.stock_location_id) {
      info.stock_location_id = Number(info.stock_location_id);
      if (info.stock_location_id < 1) {
        info.stock_location_id = 1;
      }
    }

    if (info.customer_type_id) {
      info.customer_type_id = Number(info.customer_type_id);
      if (info.customer_type_id < 1) {
        info.customer_type_id = 1;
      }
    }

    if (info.installation_state_id) {
      info.installation_state_id = Number(info.installation_state_id);
      if (info.installation_state_id < 1) {
        info.installation_state_id = 1;
      }
    }
    let body = JSON.stringify(info);
    // console.log(body);
    return this.api.Put(url, body).pipe(map(() => info));
  }

  writeStockInformation(hubIds: number[], info: HubInformation) {
    let url = `${HubService.BaseURL}stock`;

    if (info.seai_trial_group) {
      info.seai_trial_group = Number(info.seai_trial_group);
    }

    if (info.stock_status_id) {
      info.stock_status_id = Number(info.stock_status_id);
      if (info.stock_status_id < 1) {
        info.stock_status_id = 1;
      }
    }

    if (info.stock_location_id) {
      info.stock_location_id = Number(info.stock_location_id);
      if (info.stock_location_id < 1) {
        info.stock_location_id = 1;
      }
    }

    if (info.customer_type_id) {
      info.customer_type_id = Number(info.customer_type_id);
      if (info.customer_type_id < 1) {
        info.customer_type_id = 1;
      }
    }

    if (info.installation_state_id) {
      info.installation_state_id = Number(info.installation_state_id);
      if (info.installation_state_id < 1) {
        info.installation_state_id = 1;
      }
    }
    let body = JSON.stringify({ hubIds: hubIds, changes: info });
    console.log(body);
    return this.api.Post(url, body);
  }

  writeBoiler(boiler: BoilerDetails): Observable<BoilerDetails> {
    let url = `${this.oneHubURL}` + boiler.hubid + '/boiler';
    let body = JSON.stringify(boiler);
    // console.log(body);
    return this.api.Put(url, body).pipe(map(() => boiler));
  }

  writeFuelFill(fill: FuelFill): Observable<FuelFill> {
    let url = `${this.oneHubURL}` + fill.hubid + '/fuel_fill';
    let body = JSON.stringify(fill);
    // console.log(body);
    return this.api.Put(url, body).pipe(map(() => fill));
  }

  deleteFuelFill(fill: FuelFill): Observable<string> {
    let url = `${this.oneHubURL}` + fill.hubid + '/fuel_fill/' + fill.fill_timestamp;
    let body = JSON.stringify(fill);
    // console.log(body);
    return this.api.Delete(url).pipe(map(() => ""));
  }

  writeLeadsDetails(info: LeadDetails[]) {
    let url = `${HubService.BaseURL}leads`;
    let body = JSON.stringify({ changes: info });
    return this.api.Post(url, body);
  }

  uploadLeads(file: any) {
    let headers = new HttpHeaders({
      'Content-Type': file.type,
    });
    return this.api.Put(HubService.BaseURL + 'leads/upload', file, headers);
  }

  addLead(lead: LeadDetails) {
    let url = `${HubService.BaseURL}leads/add`;
    let body = JSON.stringify(lead);
    return this.api.Put(url, body);
  }

  uploadInstallationFile(file: File, company: string) {
    let headers = new HttpHeaders({
      'Content-Type': file.type,
      'Company': company
    });
    console.log(`Uploading File`);
    console.log(file);
    return this.api.Put(HubService.BaseURL + 'installs/upload', file, headers);
  }

  writeIssue(issue: HubIssue): Observable<HubIssue> {
    let url = `${HubService.BaseURL}issue/`;
    if (!!issue.issue_id) {
      url += issue.issue_id;
    } else {
      url += 'new';
    }
    issue.hubid = Number(issue.hubid);
    issue.source = Number(issue.source);
    issue.status = Number(issue.status);
    if (issue.status < 1) {
      issue.status = 1;
    }
    if (issue.source < 1) {
      issue.source = 1;
    }
    if (!!issue.action_due) {
      try {
        issue.action_due = new Date(issue.action_due).toISOString();
      } catch (e) {
        console.log('Error reading \'' + issue.action_due + '\' as date: ' + e);
      }
    }
    return this.api.Put(url, JSON.stringify(issue)).pipe(map(() => issue));
  }

  writeIssueAction(issue_action: IssueAction): Observable<IssueAction> {
    let url = `${HubService.BaseURL}issue/`;
    if (!!issue_action.issue_id) {
      url += issue_action.issue_id + '/action';
    } else {
      return;
    }
    issue_action.issue_id = Number(issue_action.issue_id);
    issue_action.action_type = Number(issue_action.action_type);
    if (issue_action.action_type < 1) {
      issue_action.action_type = 1;
    }
    return this.api.Put(url, JSON.stringify(issue_action)).pipe(map(() => issue_action));
  }

  getHubCounts(): Observable<HubCount[]> {
    return this.api.Get(this.hubCountsURL).pipe(
      map(HubService.extractStandardList),
      catchError(HubService.LogFetchError)
    );
  }

  getIssueCounts(): Observable<IssueCount[]> {
    return this.api.Get(HubService._BaseURL + 'hubs/issue_counts').pipe(
      map(HubService.extractStandardList),
      catchError(HubService.LogFetchError)
    );
  }

  getIssueTagCounts(status: string): Observable<IssueTagCount[]> {
    return this.api.Get(HubService._BaseURL + 'hubs/issue_tags/' + status).pipe(
      map(HubService.extractStandardList),
      catchError(HubService.LogFetchError)
    );
  }

  getIssueByPriority(): Observable<IssueCount[]> {
    return this.api.Get(HubService._BaseURL + 'hubs/issue_priority_counts').pipe(
      map(HubService.extractStandardList),
      catchError(HubService.LogFetchError)
    );
  }
  getVersionCounts(): Observable<VersionCount[]> {
    return this.api.Get(this.versionCountsURL).pipe(
      map(HubService.extractStandardList),
      catchError(HubService.LogFetchError)
    );
  }

  postLeads(Atype: string, Aoffset: number, Astep: number, SearchParams: LeadDetails): Observable<LeadDetails[]> {
    let url = `${HubService.BaseURL}leads/load`;
    let body = JSON.stringify({ leads_type: Atype.toString(), offset: Aoffset.toString(), step: Astep.toString(), search: SearchParams });
    return this.api.Post(url, body).pipe(
      map(HubService.extractStandardList),
      catchError(HubService.LogFetchError)
    );
  }

  postEcms(Atype: string, Aoffset: number, Astep: number, SearchParams: EcmsEntry): Observable<EcmsEntry[]> {
    let url = `${HubService.BaseURL}ecms`;
    let body = JSON.stringify({ ecms_type: Atype.toString(), offset: Aoffset.toString(), step: Astep.toString(), search: SearchParams });
    return this.api.Post(url, body).pipe(
      map(HubService.extractStandardList),
      catchError(HubService.LogFetchError)
    );
  }

  postInstallations(Atype: string, Aoffset: number, Astep: number, SearchParams: Installation): Observable<Installation[]> {
    let url = `${HubService.BaseURL}installations`;
    let body = JSON.stringify({ leads_type: Atype.toString(), offset: Aoffset.toString(), step: Astep.toString(), search: SearchParams });
    return this.api.Post(url, body).pipe(
      map(HubService.extractStandardList),
      catchError(HubService.LogFetchError)
    );
  }

  postInstallationsCsv(uid: number[]): Observable<Installation[]> {
    let url = `${HubService.BaseURL}installations/csv`;
    let body = JSON.stringify({ uid_list: uid });
    return this.api.Post(url, body).pipe(
      map(HubService.extractStandardList),
      catchError(HubService.LogFetchError)
    );
  }

  writeInstallationDetails(info: Installation[]) {
    let url = `${HubService.BaseURL}installation`;
    let body = JSON.stringify({ changes: info });
    return this.api.Post(url, body);
  }

  postLeadsCount(Atype: string, Aoffset: number, Astep: number, SearchParams: LeadDetails, Acount: boolean): Observable<StandardResponse> {
    let url = `${HubService.BaseURL}leads/load`;
    let body = JSON.stringify({ leads_type: Atype.toString(), offset: Aoffset.toString(), step: Astep.toString(), search: SearchParams, count: Acount });
    return this.api.Post(url, body).pipe(map((res) => <StandardResponse>(res)));
  }

  postLeadsUID(Atype: string, SearchParams: LeadDetails): Observable<StandardResponse> {
    let url = `${HubService.BaseURL}leads/load`;
    let body = JSON.stringify({ leads_type: Atype.toString(), offset: null, step: null, search: SearchParams, count: false, uid: true });
    return this.api.Post(url, body).pipe(map((res) => <StandardResponse>(res)));
  }

  postLeadsCsv(uid: number[]): Observable<LeadDetails[]> {
    let url = `${HubService.BaseURL}leads/csv`;
    let body = JSON.stringify({ uid_list: uid });
    return this.api.Post(url, body).pipe(
      map(HubService.extractStandardList),
      catchError(HubService.LogFetchError)
    );
  }

  postInstallationsCount(Atype: string, Aoffset: number, Astep: number, SearchParams: Installation, Acount: boolean): Observable<StandardResponse> {
    let url = `${HubService.BaseURL}installations`;
    let body = JSON.stringify({ leads_type: Atype.toString(), offset: Aoffset.toString(), step: Astep.toString(), search: SearchParams, count: Acount, mprn: false });
    return this.api.Post(url, body).pipe(map((res) => <StandardResponse>(res)));
  }

  postEcmsCount(Atype: string, Aoffset: number, Astep: number, SearchParams: EcmsEntry, Acount: boolean): Observable<StandardResponse> {
    let url = `${HubService.BaseURL}ecms`;
    let body = JSON.stringify({ ecms_type: Atype.toString(), offset: Aoffset.toString(), step: Astep.toString(), search: SearchParams, count: Acount, mprn: false });
    return this.api.Post(url, body).pipe(map((res) => <StandardResponse>(res)));
  }

  postEcmsMPRN(Atype: string, SearchParams: EcmsEntry): Observable<StandardResponse> {
    let url = `${HubService.BaseURL}ecms`;
    let body = JSON.stringify({ ecms_type: Atype.toString(), offset: null, step: null, search: SearchParams, count: false, mprn: true });
    return this.api.Post(url, body).pipe(map((res) => <StandardResponse>(res)));
  }

  postEcmsCsv(mprns: number[]): Observable<EcmsEntry[]> {
    let url = `${HubService.BaseURL}ecms/csv`;
    let body = JSON.stringify({ ecms_list: mprns });
    return this.api.Post(url, body).pipe(
      map(HubService.extractStandardList),
      catchError(HubService.LogFetchError)
    );
  }

  getSignUps(): Observable<LeadDetails[]> {
    return this.api.Get(HubService.BaseURL + 'signups').pipe(
      map(HubService.extractStandardList),
      catchError(HubService.LogFetchError)
    );
  }

  getQueryURLs(): Observable<string[]> {
    let url = ApiService.AdminBaseURL + `query_urls`
    return this.api.Get(url).pipe(
      map((response: StandardResponse) => response.list as string[]),
      catchError(HubService.getError)
    );
  }

  getQueries(endpoint: string): Observable<DatabaseQuery[]> {
    let url = ApiService.AdminBaseURL + `queries`
    if (!endpoint) {
      url += '/self';
    } else {
      url += '/' + encodeURIComponent(endpoint);
    }
    return this.api.Get(url).pipe(
      map((response: StandardResponse) => {
        let data: Object = response.data;
        let keys = Object.keys(data);
        let values: DatabaseQuery[] = [];
        keys.forEach(k => values.push(data[k]));
        return values;
      }),
      catchError(HubService.getError)
    );
  }

  updateFirmware(firmware: FirmwareDetails): Observable<FirmwareDetails> {
    if (!firmware.uid) {
      return throwError('Cannot write firmware without firmwareid');
    }
    let url = `${HubService.BaseURL}firmware/${firmware.uid}`;
    return this.api.Put(url, JSON.stringify(firmware)).pipe(map(() => firmware));
  }

  updateFirmwareSet(firmwareSet: FirmwareSet): Observable<FirmwareSet> {
    if (!firmwareSet.fwsetid) {
      return throwError('Cannot write firmwareset without fwsetid');
    }
    let url = `${HubService.BaseURL}firmwareset/${firmwareSet.fwsetid}`;
    return this.api.Put(url, JSON.stringify(firmwareSet)).pipe(map(() => firmwareSet));
  }

  updateFirmwareRoute(fwRoute: FirmwareRoute): Observable<FirmwareRoute> {
    let url = `${HubService.BaseURL}firmwareroute/${fwRoute.uid}`;
    if (fwRoute.uid < 1) {
      url = `${HubService.BaseURL}firmwareroute/new`;
    }
    return this.api.Put(url, JSON.stringify(fwRoute)).pipe(map(() => fwRoute));
  }

  sendMessage(hubIDs: number[], msg: HubMessage): Observable<StandardResponse> {
    msg.mail_type = Number(msg.mail_type);
    let msgToSend = <Object>(msg);
    msgToSend['hubids'] = hubIDs;
    return this.api.Post(HubService.hubURL(0) + `/send_messages`,
      JSON.stringify(msgToSend)).pipe(map((res) => <StandardResponse>(res)));
  }

  setSmartTechFlags(hubID: number, dutyCycle: boolean, timeShaving: boolean, peakSuppression: boolean): Observable<StandardResponse> {
    let smartTech = {
      hubid: hubID,
      duty_cycle: dutyCycle,
      time_shaving: timeShaving,
      peak_temp_suppression: peakSuppression
    };
    return this.api.Post(HubService.hubURL(0) + `/update_smart_tech`,
      JSON.stringify(smartTech)).pipe(map((res) => <StandardResponse>(res)));
  }

  issueSearch(term: string): Observable<HubIssue[]> {
    let url = HubService.BaseURL + 'issues/find/' + `${term}`;
    return this.api.Get(url).pipe(
      map(function (response: StandardResponse) {
        if (response.status !== 'success') {
          throw Error(`Search failed :-(`);
        }
        return response.list as HubIssue[];
      })
    );
  }

  uploadECMS(ecms: EcmsEntry): Observable<StandardResponse> {
    let url = HubService.BaseURL + 'ecms/upload'
    let body = JSON.stringify(ecms);
    return this.api.Put(url, body).pipe(map((res) => <StandardResponse>(res)));
  }

  removeECMS(ecms: EcmsEntry): Observable<StandardResponse> {
    let url = HubService.BaseURL + 'ecms/remove'
    let body = JSON.stringify(ecms);
    return this.api.Put(url, body).pipe(map((res) => <StandardResponse>(res)));
  }


}
